import { TBox, TTransformMode, TGridState } from './types';
import box from './box';
import { number } from 'yup';

const defaultOptions = {
  defaultAccuracy: 10
};

export type ReactRNDDragType =
  | 'bottomRight'
  | 'bottomLeft'
  | 'topRight'
  | 'topLeft'
  | 'top'
  | 'left'
  | 'bottom'
  | 'right';

export const getSnapMode = (dir: ReactRNDDragType): TTransformMode => {
  switch (dir) {
    case 'bottomRight':
      return 'resizeBottomRight';
    case 'bottomLeft':
      return 'resizeBottomLeft';
    case 'topRight':
      return 'resizeTopRight';
    case 'topLeft':
      return 'resizeTopLeft';
    case 'left':
      return 'resizeLeft';
    case 'right':
      return 'resizeRight';
    case 'bottom':
      return 'resizeBottom';
    case 'top':
      return 'resizeTop';
    default:
      return 'move';
  }
};

const isCloseToValue =
  (thresold = 0.01) =>
  (a: number, b: number) =>
    Math.abs(a - b) < thresold;

const gridState: Partial<TGridState> = {};

type SubscriberFn = (state: Partial<TGridState>) => void;
type SubscriberState = { list: Array<SubscriberFn> };
const subscriberState: SubscriberState = {
  list: []
};

const notify = () => {
  subscriberState.list.forEach(fn => {
    fn(gridState);
  });
};

export const getLastSnapState = () => gridState;

export const subscribe = (fn: SubscriberFn) => {
  if (subscriberState.list.indexOf(fn) < 0) {
    subscriberState.list.push(fn);
  }
};
export const unsubscribe = (fn: SubscriberFn) => {
  const i = subscriberState.list.indexOf(fn);
  if (i >= 0) {
    subscriberState.list.splice(i, 1);
  }
};

export const setSnapItem = (item: TBox) => {
  gridState.item = item;
};

export const setSnapItemCoordinates = (x: number, y: number) => {
  if (gridState.item) {
    gridState.item.x = x;
    gridState.item.y = y;
    gridState.item = box.fromItem(gridState.item);
  }
};

export const setSnapItemSize = (width: number, height: number) => {
  if (gridState.item) {
    gridState.item.width = width;
    gridState.item.height = height;
    gridState.item = box.fromItem(gridState.item);
  }
};

export const enableSnap = (mode: TTransformMode, item: TBox, list: TBox[]) => {
  gridState.state = 'running';
  gridState.item = item;
  gridState.otherItems = list;
  gridState.mode = mode;
};

export const disableSnap = () => {
  gridState.state = 'stopped';
  if (gridState.item && gridState.otherItems && gridState.mode) {
    gridState.lastTransformResult = box.pickTransformed(
      gridState.item,
      gridState.otherItems,
      isCloseToValue(defaultOptions.defaultAccuracy),
      gridState.mode
    );
  }
};

export const snapToElements = (
  item: TBox,
  list: TBox[],
  mode: TTransformMode,
  accuracy?: number
) => {
  return box.pickTransformed(
    item,
    list,
    isCloseToValue(accuracy || defaultOptions.defaultAccuracy),
    mode
  );
};

const loop = () => {
  try {
    if (gridState.item && gridState.otherItems && gridState.mode) {
      if (gridState.state === 'calculated' || gridState.state === 'inactive') {
        window.requestAnimationFrame(loop);
        return;
      }
      gridState.lastTransformResult = box.pickTransformed(
        gridState.item,
        gridState.otherItems,
        isCloseToValue(defaultOptions.defaultAccuracy),
        gridState.mode
      );
      if (gridState.state === 'stopped') {
        gridState.state = 'calculated';
      }
      notify();
    }
  } catch (e) {
    console.error(e);
  }
  window.requestAnimationFrame(loop);
};

window.requestAnimationFrame(loop);
